import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { getApiErrorMessage, getApiResponseMessage } from '~/utils/getApiErrorMessage'
import { warmFingerprintWhenIdle } from '~/utils/warmFingerprint'
import type { FingerprintApi, WithFingerprint } from '~/types/fingerprint'
import { translate } from '#layers/base/i18n/t'
import { bucketMultiplier, bucketPayout } from '@aurumrun/analytics'

// Sentinel id used on the synthetic MyBet-shaped row exposed by
// `visitorWinBet`. Win-screen surfaces (MyWin, WinScreen) check this id to
// branch to visitor-specific copy + CTA. Single source of truth — do NOT
// inline the literal string anywhere else.
export const VISITOR_WIN_ID = 'visitor-free-round'

export interface VisitorState {
  visitorId: string | null
  hasUsedFreeRound: boolean
  freeRoundBalance: number       // XAUT
  freeRoundStatus: 'pending' | 'active' | 'cashed_out' | 'lost' | null
  freeRoundCashoutMultiplier: number | null
  freeRoundRoundNumber: number | null
  /**
   * True when any user_id linked to this visitor's fingerprint has a prior
   * FREE_BET_BLOCKED anti-abuse event. Surfaces that promise a free bet (the
   * header Earn-Free-Bets pill, the "+1 free $1 bet" copy in LoginModal)
   * hide themselves when this is set.
   */
  freeBetsBlocked: boolean
}

export const useVisitorStore = defineStore('visitor', () => {
  const config = useRuntimeConfig()
  const identity = useIdentity()

  // State
  const visitorId = ref<string | null>(identity.state.value.visitor_id)
  const hasUsedFreeRound = ref(false)
  const freeRoundBalance = ref(0)
  const freeRoundStatus = ref<VisitorState['freeRoundStatus']>(null)
  const freeRoundCashoutMultiplier = ref<number | null>(null)
  const freeRoundRoundNumber = ref<number | null>(null)
  // Exact XAUT stake for the active free round (returned by /visitor-free-round).
  // Lets the live cashout preview compute profit the same way the backend does
  // (profit = stakeXaut × (mult − 1)), so it matches the credited amount instead
  // of assuming a flat $1 stake (which diverges by the client/backend price gap).
  const freeRoundStakeXaut = ref<number | null>(null)
  const freeBetsBlocked = ref(false)
  const canShowFreeBet = ref(false)
  const visitorAlreadyClaimed = ref(false)
  const isRegistered = ref(false)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Locally-queued free round intent — set when the user clicks "Play Free"
  // while the round is running/crashed. The backend rejects free-round
  // placement outside the betting phase, so the request is held here and
  // auto-fired by a watcher when the next betting phase starts.
  // `undefined` = no queue; `null` = queued with manual cashout; number = queued with auto-cashout target.
  const pendingFreeRoundAutoCashout = ref<number | null | undefined>(undefined)
  const hasQueuedFreeRound = computed(() => pendingFreeRoundAutoCashout.value !== undefined)

  // Getters
  // Derived from /visitor-eligibility — no FP call needed for this check.
  // `identity.isIneligibleForFreeBet` is the sticky-localStorage flag set
  // after the user acknowledged the FreeBetBlockedModal — once true, the
  // visitor permanently sees "Sign Up to play" instead of another "Play
  // Free" button, even after a hard reload.
  const isEligibleForFreeRound = computed(() =>
    isRegistered.value &&
    canShowFreeBet.value &&
    !hasUsedFreeRound.value &&
    !identity.isRegistered.value &&
    !identity.isIneligibleForFreeBet.value
  )

  const hasFreeRoundWinnings = computed(() => freeRoundBalance.value > 0)

  const freeRoundActive = computed(() =>
    freeRoundStatus.value === 'pending' || freeRoundStatus.value === 'active'
  )

  // Synthetic bet row used by the win-screen overlay and MyWin sidebar card
  // to render a visitor's free-round cashout. The visitor flow never inserts
  // into `bets`/`liveBets` and never touches `gameStore.myBets`, so without
  // this synthesized shape the standard win UI surfaces (which all read from
  // myBets) stay empty after a visitor cashes out.
  const visitorWinBet = computed(() => {
    if (freeRoundStatus.value !== 'cashed_out') return null
    const multiplier = freeRoundCashoutMultiplier.value
    if (multiplier == null || !Number.isFinite(multiplier) || multiplier <= 1) return null
    const profit = freeRoundBalance.value
    if (profit <= 0) return null
    const stake = profit / (multiplier - 1)
    return {
      id: VISITOR_WIN_ID,
      amount: stake,
      autoCashout: null as number | null,
      status: 'cashed_out' as const,
      cashoutMultiplier: multiplier,
      payout: stake + profit,
      isFreeBet: true,
      slot: 1 as const,
      capReason: null as 'max_win_cap' | 'round_cap' | null,
      payoutStatus: 'full' as const,
    }
  })

  // Visitor endpoints use getAdminClient() (no user auth needed).
  // The Supabase gateway requires the anon key — we send BOTH `apikey:` and
  // `Authorization: Bearer` so it works with the legacy JWT anon key AND the
  // modern `sb_publishable_...` key format (Kong validates either).
  const visitorHeaders = computed(() => {
    const key = config.public.supabaseAnonKey as string
    return {
      apikey: key,
      Authorization: `Bearer ${key}`,
    }
  })

  // -------- Actions --------

  /**
   * Page-load eligibility check. NO Fingerprint Pro call here — uses the
   * `visitor_id` flag from localStorage (if any) plus the request IP server-
   * side. Returns whether to show the free-bet button.
   *
   * Sets `isRegistered=true` to mark that the store has been initialized
   * (keeps backward-compat with existing components that gate on this).
   */
  async function checkEligibility(): Promise<void> {
    isLoading.value = true
    error.value = null
    try {
      // Use the FP-derived visitorId ONLY if the plugin already has one
      // cached from a prior identify() call (e.g. the user already
      // registered in this session). We deliberately do NOT trigger a
      // fresh fp.get() here — Pro v4 bills per identification, and the
      // identify call belongs to the register flow, not page-load
      // eligibility polling. When no cached id is available we fall
      // through to localStorage (`aurum_identity.visitor_id` from a
      // previous registered session, if any) or send nothing — backend
      // accepts an empty visitorId and runs IP-only checks.
      const { $fingerprint } = useNuxtApp() as WithFingerprint
      const fpVisitorId: string | null = $fingerprint?.getCachedVisitorId?.() ?? null
      const stored = fpVisitorId ?? identity.state.value.visitor_id
      visitorId.value = stored
      const response = await $fetch<{
        canShowFreeBet: boolean
        unclaimedBalance: number | null
        visitorAlreadyClaimed: boolean
        freeBetsBlocked: boolean
      }>(`${config.public.apiBase}/visitor-eligibility`, {
        method: 'POST',
        headers: visitorHeaders.value,
        body: stored ? { visitorId: stored } : {},
      })
      canShowFreeBet.value = response.canShowFreeBet
      freeBetsBlocked.value = response.freeBetsBlocked
      visitorAlreadyClaimed.value = response.visitorAlreadyClaimed
      // The free-bet button just became reachable, and pressing it runs
      // placeFreeRound() → identify(), which needs a started agent. Since #819
      // moved `start()` off page load, warm it here — the bet click lands inside
      // a betting window measured in seconds, and paying ~620 ms of main thread
      // there would eat a real share of it. Warming bills nothing; only
      // identify() does, and it is a no-op if another surface already warmed.
      //
      // Deferred to idle time, and that is not optional: unlike the login modal,
      // this runs on a PAGE-LOAD path (app.vue calls checkEligibility() from
      // onMounted), so calling prepare() inline would put the entropy collection
      // straight back into the load window this ticket exists to clear — on top
      // of hydration and the Pixi boot, for exactly the anonymous mobile cohort
      // #819 is about. Gated on `isEligibleForFreeRound` rather than the raw
      // response flag so the warm-up and the button share one condition.
      if (isEligibleForFreeRound.value) warmFingerprintWhenIdle($fingerprint)
      // Surface unclaimed balance for the "Sign up to claim $X" UI.
      if (response.unclaimedBalance && response.unclaimedBalance > 0) {
        freeRoundBalance.value = response.unclaimedBalance
        hasUsedFreeRound.value = true
        freeRoundStatus.value = 'cashed_out'
      }
      isRegistered.value = true
    } catch (err) {
      console.warn('[visitorStore] /visitor-eligibility failed:', err)
      error.value = getApiErrorMessage(err, 'Failed to check eligibility')
      // Fail-safe: hide free-bet button on error.
      canShowFreeBet.value = false
      isRegistered.value = true
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Backward-compat shim. Old callers used `registerVisitor(fpVisitorId)` on
   * page load; the new flow calls `checkEligibility()` instead. The `fpVisitorId`
   * parameter is ignored — the new flow reads the identity state from
   * localStorage and does NOT call Fingerprint Pro at page load.
   * @deprecated use `checkEligibility()` instead
   */
  async function registerVisitor(_fpVisitorId?: string): Promise<void> {
    void _fpVisitorId
    await checkEligibility()
  }

  /**
   * Queue a free-round intent locally so it auto-fires on the next betting
   * phase. Used when the user clicks Play Free while the current round is
   * running/crashed — the backend rejects placement outside betting phase.
   */
  function queueFreeRound(autoCashout?: number): void {
    pendingFreeRoundAutoCashout.value = autoCashout ?? null
  }

  function cancelQueuedFreeRound(): void {
    pendingFreeRoundAutoCashout.value = undefined
  }

  /**
   * Place a free-round bet. NOW calls Fingerprint Pro to obtain a fresh
   * (visitorId, requestId) pair and submits both to /visitor-free-round.
   * This is the FIRST FP call in a user's lifetime under the new flow.
   *
   * Handles the structured response shapes:
   *   - success                  → store visitor_id in localStorage
   *   - VISITOR_HAS_ACCOUNT      → store visitor_id; caller should open login modal
   *   - ALREADY_PLAYED + balance → store visitor_id; show "Sign up to claim $X"
   *   - FREE_ROUND_USED          → hide free-bet button
   *   - NOT_BETTING_PHASE        → queue for next betting phase
   */
  async function placeFreeRound(autoCashout?: number): Promise<{
    success: boolean
    error?: string
    errorCode?: string
    unclaimedBalance?: number
  }> {
    if (hasUsedFreeRound.value) {
      return { success: false, error: 'Already used free round', errorCode: 'ALREADY_PLAYED' }
    }

    // Force a FRESH Pro identification — Server API rejects identifications
    // older than ~60s. Reusing a cached requestId from page-load would fail
    // on the backend with `verify.reason='stale'`. `identify()` always calls
    // `fp.get()` for a brand-new event_id.
    // Narrow the injection itself rather than the whole NuxtApp — `$fingerprint`
    // is typed `unknown` here, and casting the container instead trips TS2352.
    const $fingerprint = useNuxtApp().$fingerprint as FingerprintApi
    let fpVisitorId: string
    let fpRequestId: string | null = null
    try {
      const fresh = await $fingerprint.identify?.()
      if (fresh) {
        fpVisitorId = fresh.visitorId
        fpRequestId = fresh.requestId
      } else {
        // Fallback: agent unavailable (no Pro key / ad-blocker) — use cached
        // value with no requestId. Backend treats absent requestId as
        // "unverified" and falls back to trusting the client visitorId.
        fpVisitorId = await $fingerprint.getVisitorId()
        fpRequestId = null
      }
    } catch (err) {
      console.error('[visitorStore] Fingerprint Pro failed:', err)
      return { success: false, error: 'Could not identify device', errorCode: 'FP_FAILED' }
    }

    // Short-circuit if Pro returned a fallback id (`fb_<uuid>`) — Pro agent
    // failed to load (ad blocker / privacy extension). Don't even submit;
    // backend would reject with FP_UNAVAILABLE anyway. Caller (BetPanel)
    // maps the errorCode to the FpFallbackModal. No sticky flag is set —
    // user can disable the blocker and refresh to recover.
    if (fpVisitorId.startsWith('fb_')) {
      return {
        success: false,
        error: 'Browser is blocking our fingerprint provider. Please disable any ad blocker or privacy extension and refresh the page.',
        errorCode: 'FP_UNAVAILABLE',
      }
    }

    try {
      const response = await $fetch<{
        success: boolean
        roundNumber?: number
        visitorId?: string
        unclaimedBalance?: number
        stakeXaut?: number
        error?: string
        reason?: string
        message?: string
      }>(`${config.public.apiBase}/visitor-free-round`, {
        method: 'POST',
        headers: visitorHeaders.value,
        body: {
          visitorId: fpVisitorId,
          fingerprintRequestId: fpRequestId ?? undefined,
          autoCashout: autoCashout || null,
        },
      })

      if (response.success) {
        // Happy path — round placed. Persist visitor_id in localStorage so we
        // never call FP again unless the user later registers/logs in.
        visitorId.value = response.visitorId ?? fpVisitorId
        identity.markPlayed(visitorId.value)
        hasUsedFreeRound.value = true
        freeRoundStatus.value = 'pending'
        freeRoundRoundNumber.value = response.roundNumber ?? null
        freeRoundStakeXaut.value = response.stakeXaut ?? null
        canShowFreeBet.value = false
        pendingFreeRoundAutoCashout.value = undefined
        useAnalytics().track('free_round_played', {
          usedAutoCashout: autoCashout ? 'true' : 'false',
        })
        return { success: true }
      }

      // Structured error paths.
      const code = response.error ?? 'UNKNOWN'
      switch (code) {
        case 'VISITOR_HAS_ACCOUNT': {
          // Visitor already registered as a user account in the past. Persist
          // visitor_id so we don't repeat the FP call. Caller should open login modal.
          if (response.visitorId) {
            visitorId.value = response.visitorId
            identity.markPlayed(response.visitorId)
          }
          canShowFreeBet.value = false
          return { success: false, error: getApiResponseMessage(response, translate('errors.game.failedToBet')), errorCode: code }
        }
        case 'ALREADY_PLAYED': {
          // Visitor played already (maybe won, maybe lost). Persist visitor_id;
          // surface unclaimedBalance to show "Sign up to claim $X" button.
          if (response.visitorId) {
            visitorId.value = response.visitorId
            identity.markPlayed(response.visitorId)
          }
          hasUsedFreeRound.value = true
          canShowFreeBet.value = false
          const balance = response.unclaimedBalance ?? 0
          if (balance > 0) {
            freeRoundBalance.value = balance
            freeRoundStatus.value = 'cashed_out'
          } else {
            freeRoundStatus.value = 'lost'
          }
          return { success: false, error: getApiResponseMessage(response, translate('errors.game.failedToBet')), errorCode: code, unclaimedBalance: balance }
        }
        case 'FREE_ROUND_USED': {
          // Identity-risk block — IP-history or blacklist. Do NOT persist
          // visitor_id (we don't fully trust this FP yet).
          canShowFreeBet.value = false
          return { success: false, error: getApiResponseMessage(response, translate('errors.game.failedToBet')), errorCode: code }
        }
        case 'NOT_BETTING_PHASE': {
          return { success: false, error: getApiResponseMessage(response, translate('errors.game.failedToBet')), errorCode: code }
        }
        default:
          return { success: false, error: getApiResponseMessage(response, translate('errors.game.failedToBet')), errorCode: code }
      }
    } catch (err) {
      const message = getApiErrorMessage(err, translate('errors.game.failedToBet'))
      console.error('[visitorStore] Failed to place free round:', err)
      return { success: false, error: message }
    }
  }

  /**
   * Cash out the visitor's free round
   */
  async function cashoutFreeRound(): Promise<{ success: boolean; payout?: number; multiplier?: number; error?: string }> {
    if (!visitorId.value) return { success: false, error: 'No visitor ID' }
    if (freeRoundStatus.value !== 'active') return { success: false, error: 'No active free round' }

    try {
      const response = await $fetch<{
        success: boolean
        payout?: number
        multiplier?: number
        balance?: number
        error?: string
      }>(`${config.public.apiBase}/visitor-cashout`, {
        method: 'POST',
        headers: visitorHeaders.value,
        body: { visitorId: visitorId.value },
      })

      if (response.success) {
        freeRoundStatus.value = 'cashed_out'
        freeRoundCashoutMultiplier.value = response.multiplier ?? null
        freeRoundBalance.value = response.balance ?? 0
        const multiplier = response.multiplier ?? 1
        // response.payout is XAUT profit; convert to USD via useXautPrice for bucketPayout
        const payoutUsd = useXautPrice().toUSD(response.payout ?? 0)
        useAnalytics().track('free_round_cashed_out', {
          multiplierBucket: bucketMultiplier(multiplier),
          payoutBucket: bucketPayout(payoutUsd),
        })
        return { success: true, payout: response.payout, multiplier: response.multiplier }
      }

      return { success: false, error: getApiResponseMessage(response, translate('errors.game.failedToCashout')) }
    } catch (err) {
      const message = getApiErrorMessage(err, translate('errors.game.failedToCashout'))
      console.error('[visitorStore] Failed to cashout free round:', err)
      return { success: false, error: message }
    }
  }

  /**
   * Mark the free round as lost (called when round crashes without cashout)
   */
  function markFreeRoundLost(): void {
    if (freeRoundStatus.value === 'active' || freeRoundStatus.value === 'pending') {
      freeRoundStatus.value = 'lost'
    }
  }

  /**
   * Re-fetch visitor state from backend. Called after a round ends to sync
   * with server-side auto-cashout results.
   *
   * Uses /visitor-eligibility (no FP). For a visitor that has played, this
   * re-checks unclaimedBalance.
   */
  async function refreshStatus(): Promise<void> {
    await checkEligibility()
  }

  /**
   * Clear visitor state (e.g., on login when balance is transferred)
   */
  function clearVisitor(): void {
    visitorId.value = null
    hasUsedFreeRound.value = false
    freeRoundBalance.value = 0
    freeRoundStatus.value = null
    freeRoundCashoutMultiplier.value = null
    freeRoundRoundNumber.value = null
    freeRoundStakeXaut.value = null
    freeBetsBlocked.value = false
    canShowFreeBet.value = false
    visitorAlreadyClaimed.value = false
    isRegistered.value = false
    error.value = null
    pendingFreeRoundAutoCashout.value = undefined
  }

  return {
    // State
    visitorId,
    hasUsedFreeRound,
    freeRoundBalance,
    freeRoundStatus,
    freeRoundCashoutMultiplier,
    freeRoundRoundNumber,
    freeRoundStakeXaut,
    freeBetsBlocked,
    canShowFreeBet,
    visitorAlreadyClaimed,
    isRegistered,
    isLoading,
    error,
    pendingFreeRoundAutoCashout,
    // Getters
    isEligibleForFreeRound,
    hasFreeRoundWinnings,
    freeRoundActive,
    hasQueuedFreeRound,
    visitorWinBet,
    // Actions
    checkEligibility,
    registerVisitor,
    placeFreeRound,
    queueFreeRound,
    cancelQueuedFreeRound,
    cashoutFreeRound,
    markFreeRoundLost,
    refreshStatus,
    clearVisitor,
  }
})
